import { assetUrl } from '@/utils/asset-url';
import { useEffect, useState } from "react";
import useModal from '@/hooks/use-modal';
import Modal from '@/dsComponents/Modal';
import { ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight, ModalHeader, ModalTitle } from '@dextech-ai/dex-design-system';
import { DataElementOutlined, ErrorOutlineOutlined, PitstopAndLicensesOutlined } from '@dextech-ai/dex-design-system/icons';
import Button from "@/componentsDS/Button/Button";
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import useClientRelationship from "@/hooks/use-client-relation/index";
import ClientTable from "./components/ClientTable/index";
import useDataExchangeRelation from "@/hooks/use-data-exchange-relation/index";
import api from '@/services/api/clientRelation.js';
import Toast from '@/components/Toast';
import useToast from '@/hooks/use-toast';
import { useSelector } from 'react-redux';
import InputDropdown from '@/dsComponents/select/InputDropdownSelect/index';
import Portal from '@/components/Portal/index';

export const renderAPIAction = (action) => {
	switch (action) {
		case "APPROVED":
			return { apiCall: api.clientRelationsApprove, successMessage: "Client relationship request approved" };
		case "REJECTED":
			return { apiCall: api.clientRelationsReject, successMessage: "Client relationship request rejected" };
		case "REVOKED":
			return { apiCall: api.clientRelationsRevoke, successMessage: "Client relationship request revoked" };
		case "CANCELLED":
			return { apiCall: api.clientRelationsCancel, successMessage: "Client relationship request cancelled" };
	}
}

const ClientPage = ({
	defaultSelectedAction = null
}) => {

	// CONSTANTS
	const title = "My Client";
	const description = "Manage the Client Relationships to allow you to provide and consume on their behalf.";
	const groupBy = "dataElementName";
	const emptyMessage = "No Client Relationship created yet.";

	// STATES
	const [refreshData, setRefreshData] = useState(false)
	const [selectedAction, setSelectedAction] = useState(defaultSelectedAction);
	const [selectedData, setSelectedData] = useState(null);
	const [selectedAllData, setSelectedAllData] = useState(null);

	// HOOKS
	const { visible: initiateActionButtonModal, show: showActionButtonModal, hide: hideActionButtonModal } = useModal();
	const { loading: clientLoading, fetchClientData, statusEnum, clientData, clientHeader, clientError } = useClientRelationship(handleAction);
	const {
		fetchOrgData,
		allOrgData,
		fetchPitstops,
		pitstops,
		fetchDataElements,
		dataElement,
		orgDataLoading,
		pitstopsLoading,
		dataElementLoading,
	} = useDataExchangeRelation();

	// Get Profile
	const profile = useSelector(state => state.auth.profile);
	const orgId = profile?.organizationId;

	// HANDLER FUNCTIONS
	const [loading, setLoading] = useState(false);
	const toastType = {
		success: "success",
		error: "error"
	}
	const { toastList, addToastMessage } = useToast();

	const { visible: initiateAddPitstopsModal, show: showAddPitstopsModal, hide: hideAddPitstopsModal } = useModal();
	const [selectPitstops, setSelectPitstops] = useState([]);

	// drodpown search
	const [dropdownSearch, setDropdownSearch] = useState({
		pitstop: '',
	})

	const handleDropdownSearch = (e) => {
		if (e?.target) {
			const { name, value } = e.target;
			setDropdownSearch(prev => ({ ...prev, [name]: value }));
		}
	}

	// FETCH DATA
	useEffect(() => {
		fetchClientData(`initiator=SERVICE_PROVIDER`);
	}, [refreshData]);

	useEffect(() => {
		if (clientError) {
			addToastMessage(clientError?.message, toastType.error);
		}
	}, [clientError]);

	useEffect(() => {
		fetchPitstops();
		fetchOrgData();
		fetchDataElements();
	}, []);

	function handleAction(data, action) {
		setSelectedAction(action);
		setSelectedData(data);
		showActionButtonModal();
	}

	function handleActionAll(data, type) {
		setSelectedAction(type);
		setSelectedAllData(data);
		showActionButtonModal();
	}

	async function handleAllSubmit() {
		if (selectedAllData) {
			for (const data of selectedAllData) {
				await handleSubmit(data);
			}
			return setRefreshData(!refreshData);
		}
	}

	async function handleSubmit(selectedData) {
		setLoading(true);
		let payload = {};
		const {
			serviceProviderRelationshipId,
			serviceProviderRelationshipInvitesId
		} = selectedData;

		const pitstopArr = pitstops?.length ===1  ?[ pitstops[0].systemId] : selectPitstops.map(item => item.id);
		payload = {
			...(serviceProviderRelationshipId && { serviceProviderRelationshipId: serviceProviderRelationshipId }),
			...(serviceProviderRelationshipInvitesId && { serviceProviderRelationshipInvitesId: serviceProviderRelationshipInvitesId }),
			...(pitstopArr.length > 0 && { pitstops:pitstopArr }),
		};

		const { apiCall, successMessage } = renderAPIAction(selectedAction);
		const [error, response] = await apiCall(payload);

		setLoading(false);
		if (error) {
			return addToastMessage(error?.message, toastType.error);
		}

		addToastMessage(response?.message ?? successMessage, toastType.success);
		hideActionButtonModal();
		hideAddPitstopsModal();
		setSelectPitstops([])
		return setRefreshData(!refreshData);
	}

	const renderButton = (action) => {
		switch (action) {
			case "APPROVED":
				return <Button
					text="Approve"
					type={ButtonType.SOLID}
					variant={ButtonVariant.STANDARD}
					data-testid="approve"
					onClick={() => {
						if (pitstops?.length > 1) {
							hideActionButtonModal();
							showAddPitstopsModal();
						}
						else {
							const pitstop = [pitstops[0]?.systemId];
							setSelectPitstops(pitstop);
							selectedData ? handleSubmit(selectedData) : handleAllSubmit()
						}
					}}
					id="my-client-modal-approve-button"
				/>
			case "REJECTED":
				return <Button
					text="Reject"
					type={ButtonType.SOLID}
					variant={ButtonVariant.DESTRUCTIVE}
					data-testid="reject"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit()
					}}
					id="my-client-modal-reject-button"
				/>
			case "REVOKED":
				return <Button
					text="Revoke"
					type={ButtonType.SOLID}
					variant={ButtonVariant.DESTRUCTIVE}
					data-testid="revoke"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit()
					}}
					id="my-client-modal-revoke-button"
				/>

			case "CANCELLED":
				return <Button
					text="Confirm"
					type={ButtonType.SOLID}
					variant={ButtonVariant.DESTRUCTIVE}
					data-testid="cancel-request"
					onClick={() => {
						selectedData ? handleSubmit(selectedData) : handleAllSubmit()
					}}
					id="my-client-modal-confirm-button"
				/>
		}
	};

	const renderMessage = (action) => {
		switch (action) {
			case "APPROVED":
				return (<div className="on-boarding__card-configure__body-content">
					{"You are about to approve this client relationship."}
					<div>
						{"Do you want to proceed to approve?"}
					</div>
				</div>)
			case "REJECTED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to reject this client relationship."}
						<div>
							{"Do you want to proceed to reject?"}
						</div>
					</div>
				)
			case "REVOKED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to revoke this client relationship."}
						<div>
							{"Do you want to proceed to revoke?"}
						</div>
					</div>
				)
			case "CANCELLED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to cancel this client relationship."}
						<div>
							{"Do you want to proceed to cancel?"}
						</div>
					</div>
				)
		}
	}

	// Custom Group Header Render Approve and Reject All
	const customGroupHeaderRender = (entries, utilities) => {
		const pendingEntries = entries.filter((entry) => (entry.serviceProviderRelationshipStatus.toUpperCase() === "PENDING" && entry.initiater === "DATA_OWNER" && entry.dataOwnerOrgId !== orgId));
		return (pendingEntries.length > 0) ? (<div className={`ds-multi-layer-table__group-header__right`}>
			<div className="ds-multi-layer-table__group__header__right__content">
				<div className="ds-multi-layer-table__group__header__right__content__reject" role="none" onClick={() => handleActionAll(pendingEntries, "REJECTED")} id="my-client-reject-all-button">Reject All</div>
				<div className="ds-multi-layer-table__group__header__right__content__approve" role="none" onClick={() => handleActionAll(pendingEntries, "APPROVED")} id="my-client-approve-all-button">Approve All</div>
			</div>
		</div>) : null;
	};

	return (
		<>
			<ClientTable
				title={title}
				description={description}
				groupBy={groupBy}
				data={clientData}
				emptyMessage={emptyMessage}
				headers={clientHeader}
				refreshData={refreshData}
				setRefreshData={setRefreshData}
				customGroupHeaderRender={customGroupHeaderRender}
				organizationItems={allOrgData.filter((org) => org.id != orgId).map((org) => { return { value: org.id, label: org.name, id: org.id } })}
				pitstopItems={pitstops.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId } })}
				dataElementContributeItems={dataElement.map((dataelement) => { return { value: dataelement.dataElementId, label: dataelement.elementName, id: dataelement.dataElementId } })}
				dataElementConsumeItems={dataElement.map((dataelement) => { return { value: dataelement.dataElementId, label: dataelement.elementName, id: dataelement.dataElementId } })}
				clientLoading={clientLoading}
				orgDataLoading={orgDataLoading}
				pitstopsLoading={pitstopsLoading}
				dataElementLoading={dataElementLoading}
			/>

			{/* MODAL FOR DIFFERENT ACTIONS */}
			<Modal visible={initiateActionButtonModal} close={hideActionButtonModal} dismissClickOutsideModal={false}>
				<ModalHeader icon={selectedAction === statusEnum.APPROVED ? <DataElementOutlined data-ds-color="primary-dark" /> : <ErrorOutlineOutlined color="error" />}>
					<ModalTitle>Client Consent</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{renderMessage(selectedAction)}
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							type={ButtonType.GHOST}
							variant={selectedAction === statusEnum.APPROVED? ButtonVariant.STANDARD : ButtonVariant.DESTRUCTIVE}
							data-testid="cancel"
							onClick={() => {
								hideActionButtonModal();
							}}
							id="my-client-modal-cancel-button"
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						{renderButton(selectedAction)}
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			{/* MODAL FOR ADD PITSTOPS */}
			<Modal visible={initiateAddPitstopsModal} close={hideAddPitstopsModal} dismissClickOutsideModal={false}>
				<ModalHeader icon={<PitstopAndLicensesOutlined data-ds-color="primary-dark" />}>
					<ModalTitle>Pitstop</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="card-configure__body__dropdown">
						<span className="card-configure__body__dropdown__title">
							Pitstop
						</span>
						<div className="card-configure__body__dropdown__option">
							<InputDropdown
								placeholder={"Select Pitstop(s) to host"}
								name={"pitstop"}
								dropdownOption={pitstops.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId } })}
								onSelectDropdownItem={setSelectPitstops}
								multipleSelection
								dropdownSelectedOption={selectPitstops}
								value={dropdownSearch.pitstop}
								onChange={handleDropdownSearch}
							/>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							type={ButtonType.GHOST}
							variant={ButtonVariant.STANDARD}
							data-testid="cancel-service-provider"
							onClick={() => {
								hideActionButtonModal();
								hideAddPitstopsModal();
							}}
							id="my-client-modal-cancel-button"
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button
							text="Confirm"
							type={ButtonType.SOLID}
							variant={ButtonVariant.STANDARD}
							data-testid="add-service-provider"
							disabled={selectPitstops.length === 0}
							onClick={() => {
								selectedData ? handleSubmit(selectedData) : handleAllSubmit()
							}}
							id="my-client-modal-confirm-button"
						/>
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Toast toastList={toastList} />

			<Portal id="app-blocker">
				{(loading) && (
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/loading.gif")} alt="Loading" />
						</div>
					</div>
				)}
			</Portal>
		</>
	)
}

export default ClientPage;
